import type { ApolloClient } from "@apollo/client";
import { GROUP_EMAIL_RECIPIENT_FETCH_CONCURRENCY } from "@/pages/people/membership-services/committees/constants";
import {
  collectUsableEmails,
  isUnusableEmail
} from "@/pages/people/membership-services/committees/_components/committeeMailto";
import {
  SECTION_GOVERNANCE_ROLE_EMAILS_QUERY,
  type SectionGovernanceRoleEmailNode,
  type SectionGovernanceRoleEmailsQueryData,
  type SectionGovernanceRoleEmailsQueryVariables
} from "../queries";

export type GovernanceEmailResult = {
  emails: string[];
  participantCount: number;
  excludedCount: number;
  truncated: boolean;
  nodes: SectionGovernanceRoleEmailNode[];
  failedSectionIds: string[];
};

async function mapSettledInPool<T, R>(
  items: readonly T[],
  concurrency: number,
  mapper: (item: T) => Promise<R>
): Promise<PromiseSettledResult<R>[]> {
  const results: PromiseSettledResult<R>[] = [];
  for (let index = 0; index < items.length; index += concurrency) {
    const chunk = items.slice(index, index + concurrency);
    results.push(...(await Promise.allSettled(chunk.map((item) => mapper(item)))));
  }
  return results;
}

export function collectEmailsFromGovernanceRoles(
  nodes: Array<SectionGovernanceRoleEmailNode | null | undefined>,
  { titleIds }: { titleIds?: readonly string[] } = {}
): Pick<GovernanceEmailResult, "emails" | "participantCount" | "excludedCount" | "nodes"> {
  const titleIdSet = titleIds === undefined ? null : new Set(titleIds);
  const seenPeople = new Set<string>();
  const rawEmails: string[] = [];
  const collectedNodes: SectionGovernanceRoleEmailNode[] = [];
  let participantCount = 0;
  let excludedCount = 0;

  for (const node of nodes) {
    if (!node) continue;
    if (titleIdSet !== null && (!node.title?.id || !titleIdSet.has(node.title.id))) continue;

    const person = node.person;
    if (!person?.id) continue;
    collectedNodes.push(node);
    if (seenPeople.has(person.id)) continue;
    seenPeople.add(person.id);
    participantCount += 1;

    if (isUnusableEmail(person.email)) {
      excludedCount += 1;
      continue;
    }

    rawEmails.push(person.email?.trim() ?? "");
  }

  return {
    emails: collectUsableEmails(rawEmails),
    participantCount,
    excludedCount,
    nodes: collectedNodes
  };
}

export async function fetchSectionGovernanceRoleEmails(
  client: ApolloClient<object>,
  sectionId: string
): Promise<{ nodes: SectionGovernanceRoleEmailNode[]; truncated: boolean }> {
  const { data } = await client.query<SectionGovernanceRoleEmailsQueryData, SectionGovernanceRoleEmailsQueryVariables>({
    query: SECTION_GOVERNANCE_ROLE_EMAILS_QUERY,
    variables: { sectionId },
    fetchPolicy: "no-cache"
  });

  if (!data.section) {
    throw new Error(`Section not found: ${sectionId}`);
  }

  return {
    nodes: data.section.governanceRoles ?? [],
    truncated: false
  };
}

/**
 * Loads raw governance roles per section. Title filtering stays with the caller
 * (`collectEmailsFromGovernanceRoles` + `assignmentRoleFilter`) so the per-section
 * cache can be reused when position selection changes.
 */
export async function fetchGovernanceEmailsForSections(
  client: ApolloClient<object>,
  { sectionIds }: { sectionIds: readonly string[] }
): Promise<
  GovernanceEmailResult & { bySection: Record<string, { nodes: SectionGovernanceRoleEmailNode[]; truncated: boolean }> }
> {
  const uniqueSectionIds = Array.from(new Set(sectionIds.filter(Boolean)));
  const settled = await mapSettledInPool(uniqueSectionIds, GROUP_EMAIL_RECIPIENT_FETCH_CONCURRENCY, (sectionId) =>
    fetchSectionGovernanceRoleEmails(client, sectionId)
  );

  const bySection: Record<string, { nodes: SectionGovernanceRoleEmailNode[]; truncated: boolean }> = {};
  const failedSectionIds: string[] = [];

  uniqueSectionIds.forEach((sectionId, index) => {
    const result = settled[index];
    if (result?.status === "fulfilled") {
      bySection[sectionId] = { nodes: result.value.nodes, truncated: result.value.truncated };
      return;
    }
    failedSectionIds.push(sectionId);
  });

  if (uniqueSectionIds.length > 0 && Object.keys(bySection).length === 0) {
    const firstRejected = settled.find((result) => result.status === "rejected");
    throw firstRejected && firstRejected.status === "rejected"
      ? firstRejected.reason
      : new Error("Email addresses not loaded");
  }

  const mergedNodes = Object.values(bySection).flatMap((result) => result.nodes);
  const truncated = failedSectionIds.length > 0 || Object.values(bySection).some((result) => result.truncated);

  return {
    ...collectEmailsFromGovernanceRoles(mergedNodes),
    truncated,
    failedSectionIds,
    bySection
  };
}
